import { useEffect, useRef } from "react";

import { Model3DBounds, Model3DLineSet, Model3DPointSet, Vec3Tuple } from "./types";

export interface Releasable {
	dispose?: () => void;
}

export interface RendererLike extends Releasable {
	forceContextLoss?: () => void;
}

export interface CameraFrame {
	center: Vec3Tuple;
	position: Vec3Tuple;
	near: number;
	far: number;
	fov: number;
}

const VIEW_DIRECTION: Vec3Tuple = [0.6, 0.45, 0.66];

export const disposeAll = (resources: Array<Releasable | null | undefined>): void => {
	resources.forEach((resource) => resource?.dispose?.());
};

/** Browsers cap live WebGL contexts at ~16, so a released renderer must also drop its context. */
export const releaseRenderer = (renderer: RendererLike | null | undefined): void => {
	if (!renderer) return;
	renderer.dispose?.();
	renderer.forceContextLoss?.();
};

export const useReleaseOnUnmount = (collect: () => Array<Releasable | null | undefined>): void => {
	const latest = useRef(collect);
	latest.current = collect;

	useEffect(() => () => disposeAll(latest.current()), []);
};

export const boundsFrom = (sets: Array<{ positions: number[] } | null | undefined>): Model3DBounds | null => {
	const min: Vec3Tuple = [Infinity, Infinity, Infinity];
	const max: Vec3Tuple = [-Infinity, -Infinity, -Infinity];
	let seen = false;

	sets.forEach((set) => {
		if (!set) return;
		for (let i = 0; i + 2 < set.positions.length; i += 3) {
			seen = true;
			for (let axis = 0; axis < 3; axis += 1) {
				const value = set.positions[i + axis];
				if (value < min[axis]) min[axis] = value;
				if (value > max[axis]) max[axis] = value;
			}
		}
	});

	if (!seen) return null;

	return { min, max, center: centerOf({ min, max }) };
};

export const centerOf = (bounds: Model3DBounds): Vec3Tuple => [
	(bounds.min[0] + bounds.max[0]) / 2,
	(bounds.min[1] + bounds.max[1]) / 2,
	(bounds.min[2] + bounds.max[2]) / 2,
];

/** Pull the camera back far enough that the bounding sphere fits the vertical field of view. */
export const frameBounds = (bounds: Model3DBounds | null | undefined, fov = 45, padding = 1.15): CameraFrame => {
	if (!bounds) {
		return { center: [0, 0, 0], position: [0, 0, 10], near: 0.1, far: 1000, fov };
	}

	const center = bounds.center ?? centerOf(bounds);
	const extent: Vec3Tuple = [
		bounds.max[0] - bounds.min[0],
		bounds.max[1] - bounds.min[1],
		bounds.max[2] - bounds.min[2],
	];
	const radius = Math.max(0.5, Math.hypot(extent[0], extent[1], extent[2]) / 2);
	const distance = (radius / Math.sin((fov * Math.PI) / 360)) * padding;

	return {
		center,
		position: [
			center[0] + VIEW_DIRECTION[0] * distance,
			center[1] + VIEW_DIRECTION[1] * distance,
			center[2] + VIEW_DIRECTION[2] * distance,
		],
		near: Math.max(0.1, distance / 500),
		far: distance * 8,
		fov,
	};
};

export const countPoints = (set: Model3DPointSet): number => Math.floor(set.positions.length / 3);

export const countSegments = (set: Model3DLineSet): number => Math.floor(set.positions.length / 6);

/**
 * three MULTIPLIES material.color by the per-vertex colour, so a tinted material silently voids a
 * colour ramp — and the default foreground token is `rgba(0,0,0,.65)`, which parses to black and
 * turned the whole b-factor gradient into a black shape on screen.
 */
export const materialColor = (hasVertexColors: boolean, explicit: string | undefined, fallback: string): string =>
	hasVertexColors ? "#ffffff" : (explicit ?? fallback);
